// The bar currently on screen. Tracked outside the element because a product swap
// briefly leaves the replaced bar in the DOM alongside the new one.
let activeStickyBar = null;

// Mobile only, matching the breakpoint the stylesheet hides the bar at.
const MOBILE_VIEWPORT = '(max-width: 749px)';

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      intersectionObserver = undefined;
      footerObserver = undefined;
      resizeObserver = undefined;
      submitStateObserver = undefined;
      variantChangeUnsubscriber = undefined;
      quantityUpdateUnsubscriber = undefined;
      isSyncing = false;

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.trigger = this.productInfo.querySelector('.product__add-to-cart-section');
        this.mainQuantityInput = this.productInfo.querySelector('.product-form__quantity .quantity__input');
        this.mainSubmitButton = this.productInfo.querySelector('.product-form__submit');

        this.priceWrapper = this.querySelector('.sticky-atc__price');
        this.quantityWrapper = this.querySelector('quantity-input');
        this.quantityInput = this.querySelector('.quantity__input');
        this.submitButton = this.querySelector('.sticky-atc__submit');
        this.spinner = this.querySelector('.loading__spinner');

        if (!this.trigger) return;

        this.observeTrigger();
        this.initQuantitySync();
        this.observeSubmitState();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.mobileViewport?.removeEventListener('change', this.onViewportChange);
        this.submitStateObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        this.setVisible(false);
      }

      // The bar takes over once the product form scrolls out of view, until the
      // footer comes into view. The negative bottom margin keeps the bar from
      // covering the form it replaces.
      observeTrigger() {
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isTriggerVisible = entry.isIntersecting;
            this.updateVisibility();
          },
          { rootMargin: '0px 0px -96px 0px' }
        );
        this.intersectionObserver.observe(this.trigger);

        // Out of the way again at the end of the page, so it doesn't cover the footer
        const footer = document.querySelector('footer.footer');
        if (footer) {
          this.footerObserver = new IntersectionObserver(([entry]) => {
            this.isFooterVisible = entry.isIntersecting;
            this.updateVisibility();
          });
          this.footerObserver.observe(footer);
        }

        this.mobileViewport = window.matchMedia(MOBILE_VIEWPORT);
        this.onViewportChange = this.updateVisibility.bind(this);
        this.mobileViewport.addEventListener('change', this.onViewportChange);

        // A longer price or an orientation change resizes the bar, so keep the value fresh.
        this.resizeObserver = new ResizeObserver(() => {
          if (this.isVisible) this.publishHeight();
        });
        this.resizeObserver.observe(this);
      }

      updateVisibility() {
        this.setVisible(this.mobileViewport.matches && this.isTriggerVisible === false && !this.isFooterVisible);
      }

      setVisible(isVisible) {
        this.isVisible = isVisible;
        this.classList.toggle('sticky-atc--visible', isVisible);

        if (isVisible) {
          activeStickyBar = this;
        } else if (activeStickyBar === this) {
          activeStickyBar = null;
        }

        document.body.classList.toggle('sticky-atc-active', activeStickyBar !== null);
        this.publishHeight();
      }

      // Bottom anchored elements outside the bar move by this, in CSS.
      publishHeight() {
        document.documentElement.style.setProperty(
          '--sticky-atc-height',
          `${activeStickyBar ? activeStickyBar.offsetHeight : 0}px`
        );
      }

      // The bar has no form of its own: its quantity mirrors the main input, which is
      // the one submitted with the form.
      initQuantitySync() {
        if (!this.quantityInput || !this.mainQuantityInput) return;

        this.quantityInput.addEventListener('change', () =>
          this.mirrorQuantity(this.quantityInput, this.mainQuantityInput)
        );
        this.mainQuantityInput.addEventListener('change', () =>
          this.mirrorQuantity(this.mainQuantityInput, this.quantityInput)
        );

        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.copyQuantityRules.bind(this));
        this.copyQuantityRules();
      }

      mirrorQuantity(source, destination) {
        if (this.isSyncing || destination.value === source.value) return;

        this.isSyncing = true;
        destination.value = source.value;
        destination.dispatchEvent(new Event('change', { bubbles: true }));
        this.isSyncing = false;
      }

      // Quantity rules are refreshed on the main input only (variant change, cart update).
      copyQuantityRules() {
        if (!this.quantityInput || !this.mainQuantityInput) return;

        ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'].forEach((attribute) => {
          const value = this.mainQuantityInput.getAttribute(attribute);
          if (value !== null) {
            this.quantityInput.setAttribute(attribute, value);
          } else {
            this.quantityInput.removeAttribute(attribute);
          }
        });

        this.quantityInput.value = this.mainQuantityInput.value;
        this.quantityWrapper?.validateQtyRules?.();
      }

      // Sold out and loading states are owned by the main submit button, so follow it.
      observeSubmitState() {
        if (!this.submitButton || !this.mainSubmitButton) return;

        this.submitStateObserver = new MutationObserver(this.mirrorSubmitState.bind(this));
        this.submitStateObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled'],
        });
        this.mirrorSubmitState();
      }

      mirrorSubmitState() {
        const isLoading = this.mainSubmitButton.classList.contains('loading');
        const isDisabled =
          this.mainSubmitButton.hasAttribute('disabled') ||
          this.mainSubmitButton.getAttribute('aria-disabled') === 'true';

        this.submitButton.toggleAttribute('disabled', isDisabled);
        this.submitButton.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
      }

      onVariantChange({ data: { sectionId, html } }) {
        if (sectionId !== this.dataset.sectionId) return;

        const updatedPrice = html.getElementById(`StickyPrice-${sectionId}`);
        if (updatedPrice && this.priceWrapper) {
          this.priceWrapper.innerHTML = updatedPrice.innerHTML;
        }
      }
    }
  );
}
